import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Dialog, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import logo from '../../assets/logo.png';
import { IMAGE_ACCEPT, isSupportedImageFile } from '../../imageUploadPolicy';
import { preprocessImage } from '../../imagePreprocessor';
import { uploadPictureFile } from '../../stores/pictureUpload';
import { agentUploadApi, createAgentLinkClient, linkErrorMessage, readLinkToken } from '../../agentLinkClient';
import { getPicturePreviewUrl, getPictureThumbnailUrl } from '../../pictureUrls';

const CONCURRENCY = 2;

function formatDateTime(value, fallback = '—') {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  const pad = n => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// Dependencies are swappable in tests (object URLs do not exist in jsdom).
export const previewDeps = {
  createUrl: file => (typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : null),
  revokeUrl: url => { if (url && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url); },
};

// Dependencies are injectable so tests can run without image decoding.
export const uploadDeps = {
  preprocessImage,
  uploadPictureFile,
};

function folderOf(file) {
  const path = file.webkitRelativePath || '';
  return path.includes('/') ? path.split('/')[0] : null;
}

function PhotoTile({ name, src, status, progress, message, onOpen, testId }) {
  const label = status === 'done' ? '' : status === 'exist' ? '已存在' : status === 'queued' ? '等待中' : status === 'failed' ? '失败' : message;
  return (
    <Box data-testid={testId} title={status === 'failed' ? `${name}：${message}` : name} sx={{ position: 'relative' }}>
      <Box
        onClick={src ? onOpen : undefined}
        sx={{
          aspectRatio: '1 / 1',
          borderRadius: 1.5,
          overflow: 'hidden',
          bgcolor: '#F3ECE3',
          cursor: src ? 'zoom-in' : 'default',
          outline: status === 'failed' ? '2px solid #C53C3C' : 'none',
        }}
      >
        {src
          ? <Box component="img" src={src} alt={name} loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', opacity: status === 'done' || status === 'exist' ? 1 : 0.6 }} />
          : <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}><CircularProgress size={18} /></Box>}
      </Box>
      {status === 'working' && <LinearProgress variant="determinate" value={progress || 0} sx={{ position: 'absolute', left: 4, right: 4, bottom: 22, borderRadius: 1 }} />}
      <Typography variant="caption" noWrap sx={{ display: 'block', mt: 0.5, color: status === 'failed' ? 'error.main' : 'text.secondary' }}>
        {label ? `${label} · ${name}` : name}
      </Typography>
    </Box>
  );
}

// Upload area shared by the sign-in-free upload page and the one-time
// selection page. Talks to /agent-links/upload with the given link client.
export function LinkUploadPanel({ client, showTitle = true, finishedHint, onProgress }) {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [existing, setExisting] = useState([]);
  const [zoomed, setZoomed] = useState(null);
  const [dragging, setDragging] = useState(false);
  const objectUrls = useRef([]);
  const queue = useRef([]);
  const running = useRef(0);
  const fileInput = useRef(null);
  const folderInput = useRef(null);

  const refreshInfo = useCallback(() => client.get('/agent-links/upload')
    .then(response => setInfo(response.data))
    .catch(err => setError(linkErrorMessage(err))), [client]);

  const refreshPictures = useCallback(() => client.get('/agent-links/upload/pictures')
    .then(response => setExisting(response.data?.pictures || []))
    .catch(() => {}), [client]);

  useEffect(() => {
    if (client) {
      refreshInfo();
      refreshPictures();
    }
  }, [client, refreshInfo, refreshPictures]);

  useEffect(() => () => objectUrls.current.forEach(url => previewDeps.revokeUrl(url)), []);

  const update = useCallback((id, patch) => {
    setItems(current => current.map(item => (item.id === id ? { ...item, ...patch } : item)));
  }, []);

  const pump = useCallback(() => {
    while (running.current < CONCURRENCY && queue.current.length > 0) {
      const item = queue.current.shift();
      running.current += 1;
      (async () => {
        try {
          update(item.id, { status: 'working', progress: 5, message: '处理图片' });
          const processed = await uploadDeps.preprocessImage(item.file, { maxDimension: info?.maxDimension });
          // The processed copy is a normal JPEG, so RAW files get a preview too.
          const localUrl = previewDeps.createUrl(processed.file);
          if (localUrl) {
            objectUrls.current.push(localUrl);
            update(item.id, { localUrl });
          }
          const result = await uploadDeps.uploadPictureFile({
            uuid: item.id,
            albumId: null,
            file: processed.file,
            originalFile: processed.originalFile,
            dimensions: processed.dimensions,
            sourceFolderName: item.folder,
            onProgress: (id, progress, message) => update(id, {
              ...(typeof progress === 'number' ? { progress } : {}),
              ...(message ? { message } : {}),
            }),
            api: agentUploadApi(client),
          });
          update(item.id, {
            status: result.status === 'exist' ? 'exist' : 'done',
            progress: 100,
            message: '',
            thumbnailUrl: getPictureThumbnailUrl(result.picture) || null,
            previewUrl: getPicturePreviewUrl(result.picture) || null,
          });
        } catch (err) {
          update(item.id, { status: 'failed', message: linkErrorMessage(err) });
        } finally {
          running.current -= 1;
          if (queue.current.length === 0 && running.current === 0) {
            refreshInfo();
            refreshPictures();
          }
          pump();
        }
      })();
    }
  }, [client, info, refreshInfo, update]);

  const addFiles = useCallback(fileList => {
    const files = Array.from(fileList || []).filter(isSupportedImageFile);
    if (files.length === 0) return;
    const added = files.map((file, index) => ({
      id: `${Date.now()}-${index}-${file.name}`,
      name: file.name,
      file,
      folder: folderOf(file),
      status: 'queued',
      progress: 0,
      message: '',
    }));
    setItems(current => [...current, ...added]);
    queue.current.push(...added);
    pump();
  }, [pump]);

  const canUpload = Boolean(info && info.subscriptionActive && !error);
  const done = items.filter(item => item.status === 'done').length;
  const skipped = items.filter(item => item.status === 'exist').length;
  const failed = items.filter(item => item.status === 'failed').length;
  const busy = items.some(item => ['queued', 'working'].includes(item.status));
  const finished = items.length > 0 && !busy;

  useEffect(() => {
    if (onProgress && info) onProgress({ uploadedPhotoCount: info.uploadedPhotoCount, busy: items.some(item => ['queued', 'working'].includes(item.status)) });
  }, [onProgress, info, items]);

  return (
        <Stack spacing={2.5}>
          {error && <Alert severity="error" data-testid="agent-upload-error">{error}</Alert>}
          {!error && !info && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>}
          {info && (
            <>
              {showTitle ? (
                <Box>
                  <Typography variant="h6" component="h1" data-testid="agent-upload-title">
                    上传照片到「{info.order.title}」
                  </Typography>
                  <Typography variant="body2" color="text.secondary" data-testid="agent-upload-summary">
                    已上传 {info.uploadedPhotoCount} 张 · 此链接仅可向该订单上传，{formatDateTime(info.expiresAt)} 前有效
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" data-testid="agent-upload-summary">
                  已上传 {info.uploadedPhotoCount} 张（最多 300 张）
                </Typography>
              )}

              {!info.subscriptionActive && (
                <Alert severity="warning" data-testid="agent-upload-expired"
                  action={info.renewUrl && <Button color="inherit" size="small" href={info.renewUrl}>去续费</Button>}>
                  会员已到期，续费后才能上传照片。
                </Alert>
              )}

              <Box
                data-testid="agent-upload-dropzone"
                onDragOver={event => { event.preventDefault(); if (canUpload) setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={event => {
                  event.preventDefault();
                  setDragging(false);
                  if (canUpload) addFiles(event.dataTransfer.files);
                }}
                sx={{
                  border: '2px dashed',
                  borderColor: dragging ? '#FF7AA2' : '#DFD0BE',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  bgcolor: dragging ? '#FFE0EC' : 'transparent',
                  opacity: canUpload ? 1 : 0.5,
                }}
              >
                <Typography variant="body1" sx={{ mb: 2 }}>把照片或整个文件夹拖到这里</Typography>
                <Stack direction="row" spacing={1.5} justifyContent="center">
                  <Button variant="contained" disabled={!canUpload} onClick={() => fileInput.current?.click()}>选择照片</Button>
                  <Button variant="outlined" disabled={!canUpload} onClick={() => folderInput.current?.click()}>选择文件夹</Button>
                </Stack>
                <input ref={fileInput} data-testid="agent-upload-files" type="file" multiple accept={IMAGE_ACCEPT} hidden
                  onChange={event => { addFiles(event.target.files); event.target.value = ''; }} />
                <input ref={folderInput} type="file" multiple hidden webkitdirectory="" directory=""
                  onChange={event => { addFiles(event.target.files); event.target.value = ''; }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                  支持 JPG、PNG、HEIC 和常见 RAW 格式；照片会先在本机压缩再上传，原图不会离开你的电脑。
                </Typography>
              </Box>

              {items.length > 0 && (
                <Typography variant="body2" data-testid="agent-upload-progress">
                  完成 {done} 张{skipped ? `，已存在跳过 ${skipped} 张` : ''}{failed ? `，失败 ${failed} 张` : ''}，共 {items.length} 张
                </Typography>
              )}
              {finished && done + skipped > 0 && (
                <Alert severity="success" data-testid="agent-upload-finished">
                  {finishedHint || '上传完成。回到对话告诉助手「传好了」，继续下一步。'}
                </Alert>
              )}

              {(items.length > 0 || existing.length > 0) && (
                <Box data-testid="agent-upload-grid" sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))',
                  gap: 1,
                  maxHeight: 520,
                  overflow: 'auto',
                }}>
                  {items.map(item => (
                    <PhotoTile
                      key={item.id}
                      testId="agent-upload-item"
                      name={item.name}
                      src={item.thumbnailUrl || item.localUrl}
                      status={item.status}
                      progress={item.progress}
                      message={item.message}
                      onOpen={() => setZoomed({ name: item.name, src: item.previewUrl || item.localUrl })}
                    />
                  ))}
                  {existing
                    .filter(picture => !items.some(item => item.status !== 'failed' && item.name === picture.fileName))
                    .map(picture => (
                      <PhotoTile
                        key={picture.id}
                        testId="agent-upload-existing"
                        name={picture.fileName}
                        src={picture.thumbnailUrl}
                        status="done"
                        onOpen={() => setZoomed({ name: picture.fileName, src: picture.previewUrl || picture.thumbnailUrl })}
                      />
                    ))}
                </Box>
              )}
              <Dialog open={Boolean(zoomed)} onClose={() => setZoomed(null)} maxWidth="md">
                {zoomed?.src && (
                  <Box component="img" src={zoomed.src} alt={zoomed.name || ''} sx={{ display: 'block', maxWidth: '100%', maxHeight: '80vh' }} />
                )}
                <Typography variant="caption" sx={{ p: 1 }}>{zoomed?.name}</Typography>
              </Dialog>
            </>
          )}
        </Stack>
  );
}

export default function AgentUploadPage() {
  const location = useLocation();
  const token = readLinkToken(location.hash);
  const client = useMemo(() => (token ? createAgentLinkClient(token) : null), [token]);
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FFFBF7', px: 2, py: { xs: 3, sm: 6 } }}>
      <Paper elevation={0} sx={{ maxWidth: 720, mx: 'auto', p: { xs: 3, sm: 4 }, borderRadius: 3, border: '1px solid #EFE2D4' }}>
        <Stack spacing={2.5}>
          <Box component="img" src={logo} alt="PicSect" sx={{ height: 32, alignSelf: 'flex-start' }} />
          {client
            ? <LinkUploadPanel client={client} />
            : <Alert severity="error" data-testid="agent-upload-error">链接不完整，请回到对话让助手重新生成。</Alert>}
        </Stack>
      </Paper>
    </Box>
  );
}
